import React, { useRef, useState } from 'react';
import { useMediaQuery } from '@mui/material';
import { useEventTracking } from '@services/statsig';

import { useScheduleReadOnly } from '../../../../hooks/useScheduleReadOnly';

import { BaseEvent } from './components/BaseEvent';
import { ExpandedCard } from './components/ExpandedCard';
import { getEventIcon } from './utils/getEventIcon';
import { useActionMessage, useEventStyling } from './hooks';
import type { EventComponentProps } from './types';

/**
 * EventComponent - Container/orchestrator for calendar events
 *
 * Determines the appropriate variant, color treatment, and icon based on event.resource properties.
 * Sets CSS variables for variant styling and renders BaseEvent + ExpandedCard.
 *
 * Architecture:
 * - Variant (standard/actionable/block) determines CSS variable values
 * - Color treatment determines visual appearance (completion/type state)
 * - Icon is contextual based on event properties
 * - CSS variables provide automatic style sharing between BaseEvent and ExpandedCard
 */
export const EventComponent = ({
  event,
  viewMode,
  onMouseEnter,
  onMouseLeave,
}: EventComponentProps) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const eventRef = useRef<HTMLDivElement>(null);

  // Detect touch devices - disable hover expansion on touch
  const isTouch = useMediaQuery('(hover: none), (pointer: coarse)');
  const isReadOnly = useScheduleReadOnly();

  // Analytics tracking
  const { appointment: appointmentTracking } = useEventTracking();

  // Get styling and content separately (separation of concerns)
  const { variant, cssVars } = useEventStyling(event);
  const actionMessage = useActionMessage(event);
  const icon = getEventIcon(event);

  // Helper to get actionable event type for analytics
  const getActionableEventType = (): 'suggested' | 'cancelled' | 'reschedule_requested' | null => {
    if (event.resource.status === 'suggested') return 'suggested';
    if (event.resource.confirmation_status === 'cancelled') return 'cancelled';
    if (event.resource.confirmation_status === 'reschedule_requested') return 'reschedule_requested';
    return null;
  };

  // Event handlers
  const handleMouseEnter = (e: React.MouseEvent<HTMLElement>) => {
    if (isTouch) return; // Skip hover on touch devices to prevent emulated mouse events
    setIsHovered(true);
    if (onMouseEnter) {
      onMouseEnter(event, e);
    }
  };

  const handleMouseLeave = () => {
    if (isTouch) return; // Skip hover on touch devices to prevent stuck hover states
    setIsHovered(false);
    if (onMouseLeave) {
      onMouseLeave();
    }
  };

  // Click handler for actionable events - opens modal
  const handleEventClick = (e: React.MouseEvent<HTMLElement>) => {
    if (isReadOnly) return;

    // Only activate modal for actionable variants
    if (variant === 'actionable') {
      e.stopPropagation(); // Prevent calendar navigation

      // Track quick action opened
      const actionableType = getActionableEventType();
      if (actionableType) {
        appointmentTracking.eventQuickActionOpened({
          appointment_id: event.resource.appointment_id,
          actionable_event_type: actionableType,
          view_mode: (viewMode as 'month' | 'week' | 'day') || 'month',
          is_touch_device: isTouch,
        });
      }

      setIsModalOpen(true);
      setIsHovered(false); // Close hover state when modal opens
    }
  };

  // Close modal handler
  const handleCloseModal = (e?: React.MouseEvent) => {
    e?.stopPropagation(); // Prevent event from reaching calendar
    setIsModalOpen(false);
  };

  return (
    <>
      {/* CSS variable wrapper - provides styling context for BaseEvent and ExpandedCard */}
      <BaseEvent
        event={event}
        title={event.title}
        viewMode={viewMode}
        icon={icon}
        eventRef={eventRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={variant === 'actionable' ? handleEventClick : undefined}
        sx={cssVars}
      />

      {/* Expanded card on hover/modal */}
      <ExpandedCard
        event={event}
        isHovered={isHovered}
        isModal={isModalOpen}
        isTouch={isTouch}
        anchorEl={eventRef.current}
        icon={icon}
        cssVars={cssVars}
        variant={variant}
        actionMessage={actionMessage}
        viewMode={viewMode}
        onClose={handleCloseModal}
      />
    </>
  );
};
